import React, { useRef, useState } from "react";
import { Formik, Field, Form } from "formik";
import {
    FieldWrappers,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeading,
    ModalSubHeading,
    RupeeIcon,
    CombineInputGroup
} from "../Styles/InputStyles";
import { FaUserPlus } from "react-icons/fa";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { getUserType } from "../../helpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import moment from "moment";
import CategoryCard from "../CategoryCard";
import SelectOptionModal from "../SelectOptionModal";

const PurchaseDetails = ({
    setSelectedForm,
    setFormData,
    expenseData,
    vendorList,
    setShowAddVendorForm,
    showAddVendorForm,
    addVendorMutate,
    addVendorLoading,
    setVendorMobile,
    vendorMobile,
    vendorName,
    setVendorName,
    handleVendorFormClose
}) => {
    const formikRef = useRef(null);
    const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
    const [purchaseDateCalendarOpen, setPurchaseDateCalendarOpen] = useState(false);
    const [warrantyDateCalendarOpen, setWarrantyDateCalendarOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);

    const filteredVendors = (vendorList || expenseData?.vendors || []).filter(v => v.userType === 4);

    // Prepare vendor options for SelectOptionModal
    const vendorOptions = filteredVendors.map(vendor => ({
        label: vendor.name,
        value: vendor.id,
        userType: vendor.userType
    }));

    const initialValues = {
        depreciation: "",
        purchasePrice: "",
        purchaseDate: "",
        assignVendor: "",
        warrentyExpire: "",
    };

    const validationSchema = Yup.object({
        purchaseDate: Yup.date().required("Select Purchase Date"),
        purchasePrice: Yup.number()
            .required("Enter Purchase Price")
            .min(1, "Price should not be less than 1"),
        assignVendor: Yup.number().required("Vendor is required")
    });

    const handleSubmit = (values) => {
        if (!values.assignVendor) {
            toast.error("Vendor is required")
            return;
        }
        setFormData((prevFormData) => ({
            ...prevFormData,
            purchaseDetails: values,
        }));
        setSelectedForm("3");
    };

    const handleBack = () => {
        setSelectedForm("1");
        setVendorMobile("");
        setVendorName("");
    };

    const addNewVendor = () => {
        if (!vendorName) {
            toast.error("Please fill vendor name.");
            return;
        }
        addVendorMutate({
            name: vendorName,
            mobile: vendorMobile
        });
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            innerRef={formikRef}
        >
            {({ values, setFieldValue, errors, touched }) => {

                // FIX: Move handleVendorSelect inside Formik render function
                const handleVendorSelect = (selectedVendorId) => {
                    setFieldValue("assignVendor", selectedVendorId);
                    const vendor = filteredVendors.find(v => v.id === selectedVendorId);
                    setSelectedVendor(vendor);
                };

                return (
                    <Form>
                        <ModalSubHeading>Step 2</ModalSubHeading>
                        <ModalHeading>Financial/Purchase Details</ModalHeading>
                        <ScrollableContent>
                            <Wrapper>
                                {/* Purchase Date with DatePicker */}
                                <InputGroup>
                                    <Label htmlFor="purchaseDate">Purchased/Rent Date *</Label>
                                    <FieldWrappers>
                                        <DatePicker
                                            selected={values.purchaseDate ? new Date(values.purchaseDate) : null}
                                            onChange={(date) => {
                                                setFieldValue("purchaseDate", moment(date).format("YYYY-MM-DD"));
                                                setPurchaseDateCalendarOpen(false);
                                            }}
                                            onClickOutside={() => setPurchaseDateCalendarOpen(false)}
                                            onSelect={() => setPurchaseDateCalendarOpen(false)}
                                            open={purchaseDateCalendarOpen}
                                            onInputClick={() => setPurchaseDateCalendarOpen(true)}
                                            dateFormat="yyyy-MM-dd"
                                            className="cursor-pointer items-center w-full"
                                            placeholderText="Select purchase date"
                                            showIcon
                                            icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                            showPopperArrow={false}
                                            maxDate={new Date()}
                                            onFocus={(e) => e.target.blur()}
                                            wrapperClassName="w-full"
                                            name="purchaseDate"
                                            id="purchaseDate"
                                            autoComplete="off"
                                            portalId="root-portal"
                                            popperClassName="react-datepicker-popper"
                                        />
                                    </FieldWrappers>
                                    {errors.purchaseDate && touched.purchaseDate && (
                                        <ErrorText>{errors.purchaseDate}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Purchase Price */}
                                <InputGroup>
                                    <Label htmlFor="purchasePrice">Purchased/Rent Price *</Label>
                                    <FieldWrappers>
                                        <RupeeIcon />
                                        <Field
                                            type="text"
                                            name="purchasePrice"
                                            id="purchasePrice"
                                            className="w-full truncate pl-6"
                                            onKeyPress={(e) => {
                                                if (!/[0-9]/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </FieldWrappers>
                                    {errors.purchasePrice && touched.purchasePrice && (
                                        <ErrorText>{errors.purchasePrice}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Warranty Expire Date with DatePicker */}
                                <InputGroup>
                                    <Label htmlFor="warrentyExpire">Warranty Expire</Label>
                                    <FieldWrappers>
                                        <DatePicker
                                            selected={values.warrentyExpire ? new Date(values.warrentyExpire) : null}
                                            onChange={(date) => {
                                                setFieldValue("warrentyExpire", moment(date).format("YYYY-MM-DD"));
                                                setWarrantyDateCalendarOpen(false);
                                            }}
                                            onClickOutside={() => setWarrantyDateCalendarOpen(false)}
                                            onSelect={() => setWarrantyDateCalendarOpen(false)}
                                            open={warrantyDateCalendarOpen}
                                            onInputClick={() => setWarrantyDateCalendarOpen(true)}
                                            dateFormat="yyyy-MM-dd"
                                            className="cursor-pointer items-center w-full"
                                            placeholderText="Select warranty expiry date"
                                            showIcon
                                            icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                            showPopperArrow={false}
                                            minDate={values.purchaseDate ? new Date(values.purchaseDate) : null}
                                            onFocus={(e) => e.target.blur()}
                                            wrapperClassName="w-full"
                                            name="warrentyExpire"
                                            id="warrentyExpire"
                                            autoComplete="off"
                                            portalId="root-portal"
                                            popperClassName="react-datepicker-popper"
                                        />
                                    </FieldWrappers>
                                    {errors.warrentyExpire && touched.warrentyExpire && (
                                        <ErrorText>{errors.warrentyExpire}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Depreciation Rate */}
                                <InputGroup>
                                    <Label htmlFor="depreciation">Depreciation Rate (%)</Label>
                                    <FieldWrappers>
                                        <Field
                                            type="text"
                                            name="depreciation"
                                            id="depreciation"
                                            autoComplete="off"
                                            maxLength={18}
                                            onKeyPress={(e) => {
                                                if (!/[0-9]/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </FieldWrappers>
                                    {errors.depreciation && touched.depreciation && (
                                        <ErrorText>{errors.depreciation}</ErrorText>
                                    )}
                                </InputGroup>

                                {/* Vendor Selection with CategoryCard and SelectOptionModal */}
                                {!showAddVendorForm && (
                                    <>
                                        <InputGroup>
                                            <Label htmlFor="assignVendor">Vendor *</Label>
                                            <CategoryCard
                                                type="button"
                                                selectedCategory={
                                                    selectedVendor
                                                        ? {
                                                            label: selectedVendor.name,
                                                            value: selectedVendor.id
                                                        }
                                                        : null
                                                }
                                                title="Vendor"
                                                onClick={() => setIsVendorModalOpen(true)}
                                            />
                                            {errors.assignVendor && touched.assignVendor && (
                                                <ErrorText>{errors.assignVendor}</ErrorText>
                                            )}
                                        </InputGroup>
                                        <div className="-mt-2 flex items-end justify-end">
                                            <Vendor
                                                type="button"
                                                onClick={() => {
                                                    setShowAddVendorForm(true);
                                                }}
                                            >
                                                <FaUserPlus className="mr-1 mt-0.5" />
                                                Add Vendor
                                            </Vendor>
                                        </div>
                                    </>
                                )}

                                {/* Add Vendor Form */}
                                {showAddVendorForm && (
                                    <div className="border px-2 rounded-md">
                                        <Wrapper>
                                            <CombineInputGroup>
                                                <InputGroup>
                                                    <Label htmlFor="fullName">Vendor Name *</Label>
                                                    <FieldWrappers>
                                                        <Field
                                                            type="text"
                                                            name="fullName"
                                                            id="fullName"
                                                            value={vendorName}
                                                            onChange={(e) => setVendorName(e.target.value)}
                                                        />
                                                    </FieldWrappers>
                                                </InputGroup>
                                                <InputGroup>
                                                    <Label htmlFor="mobile">Vendor Mobile</Label>
                                                    <FieldWrappers>
                                                        <Field
                                                            type="text"
                                                            name="mobile"
                                                            id="mobile"
                                                            value={vendorMobile}
                                                            maxLength={10}
                                                            onChange={(e) => setVendorMobile(e.target.value)}
                                                        />
                                                    </FieldWrappers>
                                                </InputGroup>
                                            </CombineInputGroup>
                                            <AddVendorBtn>
                                                <AddVendorCancelBtn
                                                    onClick={handleVendorFormClose}
                                                >
                                                    Cancel
                                                </AddVendorCancelBtn>
                                                <AddVendorSubmitBtn
                                                    type="button"
                                                    onClick={addNewVendor}
                                                    disabled={addVendorLoading}
                                                >
                                                    {addVendorLoading ? "Loading..." : "Add Vendor"}
                                                </AddVendorSubmitBtn>
                                            </AddVendorBtn>
                                        </Wrapper>
                                    </div>
                                )}
                            </Wrapper>
                        </ScrollableContent>
                        <ActionSection>
                            <SideCancelBtn type="button" onClick={handleBack}>
                                Back
                            </SideCancelBtn>
                            <SubmitBtn type="submit">
                                Next
                            </SubmitBtn>
                        </ActionSection>

                        {/* Vendor Selection Modal */}
                        <SelectOptionModal
                            isOpen={isVendorModalOpen}
                            onClose={() => setIsVendorModalOpen(false)}
                            title="Select Vendor"
                            options={vendorOptions}
                            selectedValue={values.assignVendor}
                            onSelect={handleVendorSelect}
                            searchPlaceholder="Search vendor..."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

const Vendor = tw.button`text-blue-500 text-sm flex hover:underline`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const InputGroup = tw.div`w-full flex flex-col mt-2 md:mt-0`;
const Label = tw.label`text-sm text-gray-600 font-normal mb-1.5`;
const AddVendorBtn = tw.div`bg-white flex justify-end py-4 px-2 gap-2`;
const AddVendorSubmitBtn = tw.button`text-xs w-20 h-7 grid place-items-center bg-primary-color hover:bg-hover-color text-white rounded-md shadow-md`;
const AddVendorCancelBtn = tw.button`text-xs w-20 h-7 grid place-items-center text-gray-600 `;

export default PurchaseDetails;